// src/components/ReceivePaymentModal.js — QuickBooks-style "Receive Payment"
// [marker: payments-deposits v1]
// One check / cash / card payment, applied to one or more invoices.
// <ReceivePaymentModal receiptId? presetInvoice? onClose onSaved />
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { AccountSelect, useAccounts } from './AccountSelect';

export const METHOD_LABELS = {
  check: 'Check', cash: 'Cash', card: 'Credit card', cardknox: 'Card (Cardknox)', wire: 'Wire', ach: 'ACH / bank transfer',
  zelle: 'Zelle', paypal: 'PayPal', money_order: 'Money order', other: 'Other',
  credit: 'Customer credit', // [marker: books-pro v1]
};
const money = (n) => `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const fmtDay = (s) => {
  if (!s) return '';
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
  return isNaN(d) ? String(s) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function ReceivePaymentModal({ receiptId, presetInvoice, onClose, onSaved }) {
  const isEdit = !!receiptId;
  const { accounts } = useAccounts();
  const banks = accounts.filter(a => a.account_type === 'bank');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [orig, setOrig] = useState(null);
  const [form, setForm] = useState({
    receivedDate: todayStr(), method: 'check', amount: presetInvoice ? String(r2(presetInvoice.balance_due)) : '',
    payerName: presetInvoice?.business_name || presetInvoice?.recipient_name || '', businessId: presetInvoice?.business_id || null,
    checkNumber: '', reference: '', memo: '', depositToAccountId: null, otherAccountId: null,
    depositDate: '', // [marker: fixes-oct21 v1] when it actually went to the bank
  });
  const [rows, setRows] = useState([]);        // open invoices shown in the apply table
  const [applied, setApplied] = useState({});  // invoiceId -> amount string
  const [manualApply, setManualApply] = useState(false);
  const [amountTouched, setAmountTouched] = useState(isEdit);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const mergeRows = (list) => setRows(cur => {
    const m = new Map(cur.map(r => [r.id, r]));
    list.forEach(r => m.set(r.id, { ...(m.get(r.id) || {}), ...r }));
    return Array.from(m.values()).sort((a, b) => String(a.issue_date).localeCompare(String(b.issue_date)) || a.id - b.id);
  });

  const loadInvoices = async ({ businessId, include }) => {
    const q = new URLSearchParams();
    if (businessId) q.set('businessId', businessId);
    if (include && include.length) q.set('include', include.join(','));
    if (!businessId && !(include && include.length)) return;
    const r = await api.get(`/receivables/open-invoices?${q}`);
    mergeRows(r.data || []);
  };

  // initial load
  useEffect(() => {
    (async () => {
      try {
        if (isEdit) {
          const r = (await api.get(`/receivables/receipts/${receiptId}`)).data;
          setOrig(r);
          setForm({
            receivedDate: r.received_date, method: r.method, amount: String(r2(r.amount)),
            payerName: r.payer_name || r.business_name || '', businessId: r.business_id,
            checkNumber: r.check_number || '', reference: r.reference || '', memo: r.memo || '',
            depositToAccountId: r.deposit_to_account_id, otherAccountId: r.other_account_id,
          });
          const ap = {};
          (r.applied || []).forEach(a => { ap[a.invoice_id] = String(r2(a.amount)); });
          setApplied(ap);
          setManualApply(true);
          await loadInvoices({ businessId: r.business_id, include: (r.applied || []).map(a => a.invoice_id) });
        } else if (presetInvoice) {
          await loadInvoices({ businessId: presetInvoice.business_id, include: [presetInvoice.id] });
          setApplied({ [presetInvoice.id]: String(r2(presetInvoice.balance_due)) });
          setManualApply(true);
        }
      } catch (e) {
        toast.error('Could not load the payment.');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // while editing an existing payment, its own amount is part of each invoice's "room"
  const roomFor = (inv) => {
    const mine = orig ? r2((orig.applied || []).filter(a => a.invoice_id === inv.id).reduce((s, a) => s + parseFloat(a.amount), 0)) : 0;
    return r2(r2(inv.balance_due) + mine);
  };

  // auto-apply oldest first until the amount runs out
  const autoApply = (amountStr, list = rows) => {
    let left = r2(amountStr);
    const ap = {};
    for (const inv of list) {
      if (left <= 0) break;
      const take = Math.min(left, roomFor(inv));
      if (take > 0) { ap[inv.id] = String(r2(take)); left = r2(left - take); }
    }
    setApplied(ap);
  };

  // tick an invoice: pay what's left of the amount (or the whole balance; with no amount yet, the amount follows the ticks)
  const toggleInvoice = (inv, room, checked) => {
    setManualApply(true);
    const next = { ...applied };
    if (!checked) delete next[inv.id];
    else {
      const others = r2(Object.entries(applied).filter(([k]) => String(k) !== String(inv.id)).reduce((s, [, v]) => s + (parseFloat(v) || 0), 0));
      const amt = r2(form.amount);
      const left = r2(amt - others);
      next[inv.id] = String(amountTouched && amt > 0 && left > 0 ? Math.min(room, left) : room);
    }
    setApplied(next);
    const total = r2(Object.values(next).reduce((s, v) => s + (parseFloat(v) || 0), 0));
    if (!amountTouched) set('amount', total ? String(total) : '');
  };

  const appliedTotal = r2(Object.values(applied).reduce((s, v) => s + (parseFloat(v) || 0), 0));
  const amount = r2(form.amount);
  const remaining = r2(amount - appliedTotal);

  const lockedMoney = !!orig && (!!orig.deposit_id || ['ticket_order', 'sponsorship', 'refund'].includes(orig.source));
  const cardLocked = !!orig && (orig.applied || []).some(a => a.cardknox_transaction_id);

  const save = async () => {
    if (!form.receivedDate) return toast.error('Enter the date received.');
    if (!isEdit && form.depositToAccountId && !form.depositDate) return toast.error('Enter the date it was deposited at the bank.');
    if (!isEdit && form.depositToAccountId && form.depositDate < form.receivedDate) return toast.error('The deposit date can’t be before the date it was received.');
    if (!lockedMoney) {
      if (!amount || amount <= 0) return toast.error('Enter the amount received.');
      if (appliedTotal > amount + 0.005) return toast.error('You applied more than the amount received.');
      if (remaining > 0.005 && !form.otherAccountId) return toast.error(`${money(remaining)} isn’t applied to an invoice — apply it, or pick an income account for the rest.`);
    }
    const body = {
      ...form,
      amount,
      applications: Object.entries(applied).filter(([, v]) => r2(v) > 0).map(([invoiceId, v]) => ({ invoiceId: parseInt(invoiceId, 10), amount: r2(v) })),
      otherAccountId: remaining > 0.005 ? form.otherAccountId : null,
    };
    setSaving(true);
    try {
      const r = isEdit ? await api.patch(`/receivables/receipts/${receiptId}`, body) : await api.post('/receivables/receipts', body);
      if (r.data?.notice) toast(r.data.notice, { icon: 'ℹ️', duration: 6000 });
      else toast.success(isEdit ? 'Payment updated.' : (form.depositToAccountId ? 'Payment recorded and deposited.' : 'Payment recorded — it’s waiting in Payments to Deposit.'));
      onSaved && onSaved(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the payment.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this payment? The invoices it paid will show as unpaid again.')) return;
    setSaving(true);
    try {
      await api.delete(`/receivables/receipts/${receiptId}`);
      toast.success('Payment deleted.');
      onSaved && onSaved(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete the payment.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1400 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Payment' : 'Receive Payment'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div> : (
            <>
              {orig?.deposit_id && (
                <Banner>Deposited on <strong>{fmtDay(orig.deposit_date)}</strong> into <strong>{orig.deposit_account_name}</strong>. You can fix the check #, payer and memo here; to change the amount or invoices, remove it from that deposit first.</Banner>
              )}
              {orig && ['ticket_order', 'sponsorship', 'refund'].includes(orig.source) && (
                <Banner>{orig.source === 'refund' ? 'This is a card refund.' : 'This payment came from an event sale.'} Its amount is managed on the event / Transactions page.</Banner>
              )}
              {orig?.status === 'historical' && (
                <Banner>This payment was recorded before Payments to Deposit started, so it isn’t waiting for a deposit.</Banner>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Received From</label>
                  <PayerPicker value={form.payerName} disabled={lockedMoney}
                               onChange={(name) => set('payerName', name)}
                               onPickBusiness={async (b) => {
                                 setForm(f => ({ ...f, payerName: b.business_name, businessId: b.id }));
                                 await loadInvoices({ businessId: b.id });
                               }} />
                  {form.businessId && <p className="form-hint" style={{ margin: '2px 0 0' }}>Linked to a member business — its open invoices are listed below.</p>}
                </div>
                <div className="form-group">
                  <label className="form-label">Check / Card Date *</label>
                  <input className="form-input" type="date" value={form.receivedDate} disabled={lockedMoney} onChange={e => set('receivedDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount *</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={form.amount} disabled={lockedMoney || cardLocked}
                         onChange={e => { setAmountTouched(true); set('amount', e.target.value); if (!manualApply) autoApply(e.target.value); }} placeholder="0.00" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select className="form-select" value={form.method} disabled={lockedMoney || cardLocked} onChange={e => set('method', e.target.value)}>
                    {Object.entries(METHOD_LABELS).filter(([k]) => k !== 'credit' && (k !== 'cardknox' || form.method === 'cardknox')).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{form.method === 'check' ? 'Check #' : 'Check # (if any)'}</label>
                  <input className="form-input" value={form.checkNumber} onChange={e => set('checkNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Reference / Card #</label>
                  <input className="form-input" value={form.reference} onChange={e => set('reference', e.target.value)} placeholder="Optional" />
                </div>
              </div>

              {/* Apply to invoices */}
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '0.85rem' }}>Apply to invoices</strong>
                  {!lockedMoney && rows.length > 0 && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setManualApply(false); autoApply(form.amount); }}>Auto-apply (oldest first)</button>
                  )}
                  {!lockedMoney && rows.length > 0 && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setManualApply(true); setApplied({}); }}>Clear</button>
                  )}
                  <span style={{ marginLeft: 'auto' }} />
                  {!lockedMoney && <InvoiceFinder onAdd={(list) => mergeRows(list)} />}
                </div>
                {rows.length === 0 ? (
                  <div style={{ padding: 14, fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                    Pick who it’s from to see their open invoices, or use “Find invoice”. A payment that isn’t for an invoice (a donation, for example) can go straight to an income account below.
                  </div>
                ) : (
                  <div style={{ maxHeight: 260, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: 'white', position: 'sticky', top: 0 }}>
                          <th style={th}></th><th style={th}>Date</th><th style={th}>Invoice</th><th style={th}>For</th>
                          <th style={{ ...th, textAlign: 'right' }}>Total</th><th style={{ ...th, textAlign: 'right' }}>Open</th>
                          <th style={{ ...th, textAlign: 'right', width: 130 }}>Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(inv => {
                          const room = roomFor(inv);
                          const val = applied[inv.id] ?? '';
                          const on = r2(val) > 0;
                          return (
                            <tr key={inv.id} style={{ borderTop: '1px solid var(--gray-100)', background: on ? '#f0fdf4' : undefined }}>
                              <td style={td}>
                                <input type="checkbox" checked={on} disabled={lockedMoney}
                                       onChange={e => toggleInvoice(inv, room, e.target.checked)} />
                              </td>
                              <td style={td}>{fmtDay(inv.issue_date)}</td>
                              <td style={{ ...td, fontFamily: 'monospace' }}>{inv.invoice_number}</td>
                              <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={[inv.business_name || inv.recipient_name, inv.items].filter(Boolean).join(' — ')}>
                                {inv.business_name || inv.recipient_name || ''}{inv.items ? <span style={{ color: 'var(--gray-500)' }}> — {inv.items}</span> : null}
                              </td>
                              <td style={{ ...td, textAlign: 'right' }}>{money(inv.total_amount)}</td>
                              <td style={{ ...td, textAlign: 'right' }}>{money(room)}</td>
                              <td style={{ ...td, textAlign: 'right' }}>
                                <input className="form-input" type="number" step="0.01" min="0" value={val} disabled={lockedMoney}
                                       style={{ height: 28, textAlign: 'right', padding: '0 6px' }}
                                       onChange={e => { setManualApply(true); const v = e.target.value; setApplied(a => ({ ...a, [inv.id]: v })); }} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 18, padding: '8px 10px', borderTop: '1px solid var(--gray-200)', fontSize: '0.85rem' }}>
                  <span>Applied: <strong>{money(appliedTotal)}</strong></span>
                  <span style={{ color: remaining < -0.005 ? 'var(--danger)' : remaining > 0.005 ? '#b45309' : 'var(--success)' }}>
                    {remaining < -0.005 ? `Over by ${money(-remaining)}` : `Not applied: ${money(remaining)}`}
                  </span>
                </div>
              </div>

              {remaining > 0.005 && !lockedMoney && (
                <div className="form-group">
                  <label className="form-label">Put the {money(remaining)} not applied to an invoice into</label>
                  <AccountSelect types={['income', 'other_income']} value={form.otherAccountId} onChange={v => set('otherAccountId', v)}
                                 placeholder="— Choose an income account (e.g. donation, misc.) —" />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Deposit To</label>
                  <select className="form-select" value={form.depositToAccountId || ''} disabled={lockedMoney || (orig && orig.status === 'historical')}
                          onChange={e => { const v = e.target.value ? parseInt(e.target.value, 10) : null; set('depositToAccountId', v); if (v && !form.depositDate) set('depositDate', form.receivedDate); }}>
                    <option value="">Payments to Deposit (deposit it later with other payments)</option>
                    {banks.map(b => <option key={b.id} value={b.id}>Straight into {b.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Memo</label>
                  <input className="form-input" value={form.memo} onChange={e => set('memo', e.target.value)} />
                </div>
              </div>
              {!isEdit && form.depositToAccountId && (
                <div className="form-group" style={{ maxWidth: 260, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>
                  <label className="form-label">Date deposited at the bank *</label>
                  <input className="form-input" type="date" value={form.depositDate} min={form.receivedDate} onChange={e => set('depositDate', e.target.value)} />
                  <p className="form-hint" style={{ margin: '4px 0 0' }}>Checks often go to the bank later — the register shows it on this date.</p>
                </div>
              )}
              {orig && !orig.deposit_id && orig.status === 'pending' && (
                <p className="form-hint" style={{ margin: 0 }}>Deposit date: <em>blank until it’s deposited</em>.</p>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isEdit && orig && !orig.deposit_id && !['ticket_order', 'sponsorship', 'refund'].includes(orig.source) && !cardLocked
            ? <button className="btn btn-danger" onClick={remove} disabled={saving}>Delete</button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving || loading}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Save Payment')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Banner({ children }) {
  return <div style={{ fontSize: '0.82rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>{children}</div>;
}

// Type a name; pick a member business from the suggestions (or keep any name)
export function PayerPicker({ value, onChange, onPickBusiness, disabled }) {
  const [q, setQ] = useState(value || '');
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setQ(value || ''); }, [value]);
  const search = (text) => {
    clearTimeout(timer.current);
    if (!text || text.trim().length < 2) { setList([]); return; }
    timer.current = setTimeout(() => {
      api.get(`/businesses?search=${encodeURIComponent(text.trim())}&limit=8`)
        .then(r => { setList(r.data?.data || []); setOpen(true); }).catch(() => {});
    }, 250);
  };
  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" value={q} disabled={disabled} placeholder="Business or person"
             onChange={e => { setQ(e.target.value); onChange(e.target.value); search(e.target.value); }}
             onFocus={() => list.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && list.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--gray-200)', borderRadius: 6, boxShadow: '0 6px 16px rgba(0,0,0,0.12)', maxHeight: 240, overflow: 'auto' }}>
          {list.map(b => (
            <div key={b.id} onMouseDown={() => { setOpen(false); setQ(b.business_name); onPickBusiness(b); }}
                 style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid var(--gray-100)' }}
                 onMouseOver={e => { e.currentTarget.style.background = 'var(--gray-50)'; }} onMouseOut={e => { e.currentTarget.style.background = 'white'; }}>
              <strong>{b.business_name}</strong>{b.email ? <span style={{ color: 'var(--gray-500)' }}> · {b.email}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Search any open invoice by number / name and add it to the apply table
export function InvoiceFinder({ onAdd }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const find = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await api.get(`/receivables/open-invoices?search=${encodeURIComponent(q.trim())}`);
      if (!r.data.length) toast('No open invoices match.', { icon: '🔎' });
      onAdd(r.data || []);
    } catch { toast.error('Search failed.'); }
    finally { setBusy(false); }
  };
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      <input className="form-input" style={{ height: 30, width: 200 }} placeholder="Find invoice # or name" value={q}
             onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); find(); } }} />
      <button type="button" className="btn btn-secondary btn-sm" onClick={find} disabled={busy}>Find</button>
    </span>
  );
}

const th = { textAlign: 'left', padding: '6px 8px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', borderBottom: '1px solid var(--gray-200)' };
const td = { padding: '5px 8px', verticalAlign: 'middle' };

export { money, r2, todayStr };
